import React from 'react';
import { Link } from 'react-router-dom';
import { Building2, ArrowLeft } from 'lucide-react';

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-[#FDFDFD]" data-testid="terms-page">
      {/* Header */}
      <header className="border-b border-zinc-200 bg-white">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="terms-logo">
            <div className="w-8 h-8 bg-[#D94E18] rounded-md flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" strokeWidth={1.5} />
            </div>
            <span className="text-lg font-semibold text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              kaya.ai
            </span>
          </Link>
          <Link to="/" data-testid="terms-back-btn" className="flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-700 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12 md:py-16">
        <h1 className="text-3xl sm:text-4xl font-semibold text-zinc-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Terms & Conditions
        </h1>
        <p className="text-sm text-zinc-400 mb-10">Last updated: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p>

        <div className="prose-custom space-y-8">
          <section>
            <h2>1. Acceptance of Terms</h2>
            <p>
              By accessing or using kaya.ai (the "Service"), you agree to be bound by these Terms & Conditions ("Terms").
              If you do not agree to all of these Terms, you must not use the Service. kaya.ai reserves the right to modify
              these Terms at any time, and your continued use of the Service constitutes acceptance of any changes.
            </p>
          </section>

          <section>
            <h2>2. Key Points — Please Read Carefully</h2>
            <div style={{background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '8px', padding: '16px', marginBottom: '16px'}}>
              <p style={{fontWeight: '600', marginBottom: '8px'}}>By creating an account and using kaya.ai, you explicitly agree that:</p>
              <ul style={{marginTop: '0'}}>
                <li><strong>All data, reports, and results are your sole responsibility</strong> from beginning to end. kaya.ai guarantees nothing regarding accuracy or completeness.</li>
                <li><strong>kaya.ai does not provide financial, accounting, tax, or legal advice.</strong> All outputs are automated suggestions only.</li>
                <li><strong>You must independently verify</strong> all financial data, classifications, tax calculations, and reports before relying on them for any purpose.</li>
                <li><strong>kaya.ai bears no liability</strong> for any errors, losses, penalties, or damages arising from your use of or reliance on the Service.</li>
              </ul>
            </div>
          </section>

          <section>
            <h2>3. Description of Service</h2>
            <p>
              kaya.ai is an AI-assisted accounting tool that processes uploaded financial documents (bank statements, CSV files,
              images) and generates suggested double-entry bookkeeping journal entries. The Service uses artificial intelligence
              to classify transactions and suggest appropriate accounting treatments.
            </p>
          </section>

          <section>
            <h2>4. No Financial or Accounting Advice</h2>
            <p>
              <strong>THE SERVICE IS PROVIDED AS A SOFTWARE TOOL ONLY AND DOES NOT CONSTITUTE FINANCIAL, ACCOUNTING, TAX,
              OR LEGAL ADVICE.</strong> kaya.ai is not a licensed accounting firm, tax advisor, auditor, or financial institution.
              All outputs generated by the Service — including but not limited to transaction classifications, journal entries,
              tax rate suggestions, VAT calculations, and financial reports — are <strong>automated suggestions</strong> that
              may contain errors, omissions, or inaccuracies.
            </p>
            <p>
              You are solely responsible for reviewing, validating, and approving all data processed through the Service.
              You should always consult a qualified accountant, tax advisor, or financial professional before making any
              financial or business decisions based on the outputs of kaya.ai.
            </p>
          </section>

          <section>
            <h2>5. Limitation of Liability</h2>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, KAYA.AI AND ITS OWNERS, OFFICERS, DIRECTORS, EMPLOYEES,
              AGENTS, AND AFFILIATES SHALL NOT BE LIABLE FOR:
            </p>
            <ul>
              <li>Any errors, inaccuracies, or omissions in the AI-generated outputs, including incorrect transaction classifications, tax calculations, journal entries, or financial reports.</li>
              <li>Any financial losses, tax penalties, regulatory fines, or other damages arising from reliance on the Service's outputs.</li>
              <li>Any indirect, incidental, special, consequential, or punitive damages, regardless of the cause of action or the theory of liability.</li>
              <li>Any loss or corruption of data uploaded to or processed by the Service.</li>
              <li>Any interruption of service, downtime, or unavailability of the platform.</li>
            </ul>
            <p>
              IN NO EVENT SHALL KAYA.AI'S TOTAL LIABILITY EXCEED THE AMOUNT PAID BY YOU TO KAYA.AI IN THE TWELVE (12) MONTHS
              PRECEDING THE EVENT GIVING RISE TO THE CLAIM, OR CHF 100, WHICHEVER IS LESS.
            </p>
          </section>

          <section>
            <h2>6. Disclaimer of Warranties</h2>
            <p>
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED,
              STATUTORY, OR OTHERWISE. KAYA.AI SPECIFICALLY DISCLAIMS ALL IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR
              A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. KAYA.AI DOES NOT WARRANT THAT:
            </p>
            <ul>
              <li>The Service will meet your specific requirements or expectations.</li>
              <li>The AI classifications, tax suggestions, or financial reports will be accurate, complete, or error-free.</li>
              <li>The Service will be uninterrupted, timely, secure, or free of viruses or other harmful components.</li>
              <li>Any errors in the Service will be corrected.</li>
            </ul>
          </section>

          <section>
            <h2>7. Your Responsibilities</h2>
            <p>By using the Service, you acknowledge and agree that:</p>
            <ul>
              <li>You are solely responsible for the accuracy and legality of all data you upload.</li>
              <li>You will review all AI-generated outputs before posting them to your ledger.</li>
              <li>You will not rely on kaya.ai as your sole source of accounting, tax, or financial truth.</li>
              <li>You will maintain your own backups of all financial data.</li>
              <li>You will comply with all applicable laws and regulations in your jurisdiction.</li>
              <li>You will not upload data belonging to third parties without their express consent.</li>
            </ul>
          </section>

          <section>
            <h2>8. Data & Security</h2>
            <p>
              You retain ownership of all data you upload to kaya.ai. We process your data solely for the purpose of providing
              the Service. While we implement industry-standard security measures, no system is 100% secure, and we cannot
              guarantee absolute security of your data. See our <Link to="/privacy" className="text-[#D94E18] hover:underline">Privacy Policy</Link> for
              more information on how we handle your data.
            </p>
          </section>

          <section>
            <h2>9. Account Termination</h2>
            <p>
              kaya.ai reserves the right to suspend or terminate your account at any time, for any reason, including but not
              limited to violation of these Terms, suspected fraudulent activity, or prolonged inactivity. Upon termination,
              your right to use the Service ceases immediately. We may, at our discretion, provide a reasonable period for you
              to export your data.
            </p>
          </section>

          <section>
            <h2>10. Indemnification</h2>
            <p>
              You agree to indemnify, defend, and hold harmless kaya.ai and its owners, officers, directors, employees, and
              agents from and against any and all claims, damages, losses, liabilities, costs, and expenses (including
              reasonable attorneys' fees) arising out of or related to your use of the Service, your violation of these Terms,
              or your violation of any applicable law or regulation.
            </p>
          </section>

          <section>
            <h2>11. Governing Law</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of Switzerland. Any disputes arising
              from or relating to these Terms or the Service shall be subject to the exclusive jurisdiction of the courts of
              Zurich, Switzerland.
            </p>
          </section>

          <section>
            <h2>12. Contact</h2>
            <p>
              If you have any questions about these Terms, please contact us at <span className="text-zinc-700 font-medium">legal@kaya.ai</span>.
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-zinc-200 bg-white">
        <div className="max-w-4xl mx-auto px-6 py-6 flex flex-col sm:flex-row justify-between items-center gap-3">
          <p className="text-xs text-zinc-400">&copy; {new Date().getFullYear()} kaya.ai. All rights reserved.</p>
          <div className="flex gap-6">
            <Link to="/terms" className="text-xs text-zinc-500 hover:text-zinc-700">Terms & Conditions</Link>
            <Link to="/privacy" className="text-xs text-zinc-500 hover:text-zinc-700">Privacy Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
